import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { RouteTheme } from 'theme/routeThemes';
import './RouteBackground.css';

export const BACKGROUND_CROSSFADE_MS = 400;
// Slow or failed artwork must not hold the previous page's scene forever.
export const BACKGROUND_READY_TIMEOUT_MS = 3000;
// Revealed artwork that loads within this window paints directly; later artwork fades in instead of popping.
export const BACKGROUND_REVEAL_GRACE_MS = 100;

// loading: hidden above the painted layer until its artwork is decoded.
// entering: fading in over the layers below it.
// revealing: its gradient is shown at once because no artwork was established below it
//   (first paint, or leaving a covered module). Its artwork stays hidden until decoded,
//   then fades in (artShown) before the layer settles.
// leaving: fading back out because the learner moved on before it was established.
type LayerPhase = 'loading' | 'entering' | 'leaving' | 'revealing' | 'settled';
type Layer = {
  id: number;
  key: string;
  render: RouteTheme['renderBackground'];
  phase: LayerPhase;
  // A revealing layer's decoded artwork is fading in.
  artShown?: boolean;
  // Left before the destination was established: artwork that never painted stays hidden
  // while the layer fades out (the layer itself knows whether its artwork painted).
  artHidden?: boolean;
  // The settled layer has been on screen, so later navigation crossfades from it.
  painted?: boolean;
};

const backgroundKeyFor = (theme: RouteTheme) => theme.backgroundKey ?? theme.key;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);

/** Resolves once an image can paint without a blank frame; failures resolve too. */
const whenImageReady = (image: HTMLImageElement): Promise<void> => {
  const decode = () => (image.naturalWidth > 0 && typeof image.decode === 'function'
    ? image.decode().catch(() => undefined)
    : Promise.resolve());
  if (image.complete) return decode();
  return new Promise<void>((resolve) => {
    const settle = () => {
      image.removeEventListener('load', settle);
      image.removeEventListener('error', settle);
      resolve();
    };
    image.addEventListener('load', settle);
    image.addEventListener('error', settle);
  }).then(decode);
};

/**
 * Nothing under a covered module scene was seen: show only the destination, reusing its
 * layer (and image) when one exists. Decoded art settles at once; pending art reveals.
 */
const showOnly = (current: Layer[], incoming: Layer): Layer[] => {
  const existing = current.map((layer) => layer.key).lastIndexOf(incoming.key);
  if (existing === -1) return [{ ...incoming, phase: 'revealing' }];
  const layer = current[existing];
  if (current.length === 1 && (layer.phase === 'settled' || layer.phase === 'revealing')) return current;
  // Artwork that never appeared still has to reveal; established artwork settles.
  const pending = layer.phase === 'loading' || layer.phase === 'revealing' || Boolean(layer.artHidden);
  return [{
    ...layer,
    artHidden: false,
    phase: pending ? 'revealing' : 'settled',
    painted: !pending && layer.phase !== 'settled' ? true : layer.painted,
  }];
};

type BackgroundLayerProps = {
  layer: Layer;
  // fadeArt: the layer's artwork was hidden while it loaded, so it should fade in.
  onReady: (id: number, fadeArt: boolean) => void;
  onEntered: (id: number) => void;
  onLeft: (id: number) => void;
  onPainted: (id: number) => void;
};

const BackgroundLayer = React.memo<BackgroundLayerProps>(({ layer, onReady, onEntered, onLeft, onPainted }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { id, phase } = layer;
  const waiting = phase === 'loading' || phase === 'revealing';
  const [concealArt, setConcealArt] = useState(false);
  const concealed = useRef(false);
  // Whether this layer's artwork has been (or is being) painted on screen.
  const artPainted = useRef(false);

  // Cached artwork is shown in the first painted frame instead of fading in from nothing.
  useLayoutEffect(() => {
    if (phase !== 'revealing') return;
    const images = Array.from(ref.current?.querySelectorAll('img') ?? []);
    if (images.every((image) => image.complete)) {
      artPainted.current = true;
      onReady(id, false);
    }
  }, [id, phase, onReady]);

  useLayoutEffect(() => {
    if (phase === 'entering' || phase === 'settled' || layer.artShown) artPainted.current = true;
  }, [phase, layer.artShown]);

  useEffect(() => {
    if (phase !== 'revealing') return undefined;
    const images = Array.from(ref.current?.querySelectorAll('img') ?? []);
    // Artwork that loads before it is concealed paints straight away, even while decoding.
    const loaded = () => {
      if (!concealed.current && images.every((image) => image.complete)) artPainted.current = true;
    };
    loaded();
    images.forEach((image) => image.addEventListener('load', loaded));
    const timer = window.setTimeout(() => {
      // Never hide artwork that has already painted.
      if (images.some((image) => !image.complete)) {
        concealed.current = true;
        setConcealArt(true);
      }
    }, BACKGROUND_REVEAL_GRACE_MS);
    return () => {
      images.forEach((image) => image.removeEventListener('load', loaded));
      window.clearTimeout(timer);
    };
  }, [phase]);

  // Decided at commit rather than render: a transition render can yield, letting the
  // browser paint the artwork before the leaving state is committed.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Complete, unconcealed artwork may already be on screen even before its load event runs.
    const images = Array.from(node.querySelectorAll('img'));
    const onScreen = artPainted.current || (!concealed.current && images.length > 0 && images.every((image) => image.complete));
    if (phase === 'leaving' && layer.artHidden && !onScreen) node.setAttribute('data-art-unseen', 'true');
    else node.removeAttribute('data-art-unseen');
  }, [phase, layer.artHidden]);

  useEffect(() => {
    if (!waiting) return undefined;
    let active = true;
    const images = Array.from(ref.current?.querySelectorAll('img') ?? []);
    const timeout = window.setTimeout(() => active && onReady(id, concealed.current), BACKGROUND_READY_TIMEOUT_MS);
    Promise.all(images.map(whenImageReady)).then(() => {
      if (!active) return;
      // Commit the hidden starting opacity so a cached image still fades in.
      ref.current?.getBoundingClientRect();
      onReady(id, concealed.current);
    });
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [id, waiting, onReady]);

  // A settled layer counts as seen once the browser has had a frame to paint it.
  useEffect(() => {
    if (phase !== 'settled' || layer.painted) return undefined;
    let second = 0;
    const first = window.requestAnimationFrame(() => {
      second = window.requestAnimationFrame(() => onPainted(id));
    });
    return () => {
      window.cancelAnimationFrame(first);
      window.cancelAnimationFrame(second);
    };
  }, [id, phase, layer.painted, onPainted]);

  const fadingArt = phase === 'revealing' && Boolean(layer.artShown);
  useEffect(() => {
    if (phase !== 'entering' && phase !== 'leaving' && !fadingArt) return undefined;
    const timer = window.setTimeout(() => (phase === 'leaving' ? onLeft(id) : onEntered(id)), BACKGROUND_CROSSFADE_MS);
    return () => window.clearTimeout(timer);
  }, [id, phase, fadingArt, onEntered, onLeft]);

  return (
    <div
      ref={ref}
      className="route-background-layer absolute inset-0"
      data-testid="route-background-layer"
      data-background={layer.key}
      data-phase={phase}
      data-conceal-art={phase === 'revealing' && concealArt && !layer.artShown ? 'true' : undefined}
      style={{
        opacity: phase === 'loading' || phase === 'leaving' ? 0 : 1,
        transition: phase === 'entering' || phase === 'leaving' ? `opacity ${BACKGROUND_CROSSFADE_MS}ms ease-out` : undefined,
      }}
    >
      {layer.render?.()}
    </div>
  );
});
BackgroundLayer.displayName = 'BackgroundLayer';

type RouteBackgroundProps = {
  theme: RouteTheme;
  // The current history entry; covered state is tracked per entry.
  locationKey: string;
  // The history entry whose route background an opaque module scene fully covered, if any.
  coveredLocationKey: React.RefObject<string | null>;
};

/**
 * Keeps the painted route artwork on screen until the next theme's artwork is
 * decoded, then fades the new layer over it. Each layer keeps its own image
 * element, so a reused img never shows old artwork with the next theme's sizing.
 * Artwork that a module scene covered was never seen, so leaving that module
 * shows the destination's own background at once instead of uncovering it.
 */
export const RouteBackground: React.FC<RouteBackgroundProps> = ({ theme, locationKey, coveredLocationKey }) => {
  const nextId = useRef(1);
  const shownLocationKey = useRef(locationKey);
  const key = backgroundKeyFor(theme);
  // The first paint has nothing to hold, so it reveals like a covered exit.
  const [layers, setLayers] = useState<Layer[]>(() => [
    { id: 0, key, render: theme.renderBackground, phase: 'revealing' },
  ]);

  // Declared before the location sync below so it still sees the entry being left.
  useLayoutEffect(() => {
    const id = nextId.current;
    nextId.current += 1;
    const incoming: Layer = { id, key, render: theme.renderBackground, phase: 'loading' };
    if (coveredLocationKey.current !== null && coveredLocationKey.current === shownLocationKey.current) {
      setLayers((current) => showOnly(current, incoming));
      return;
    }
    const instant = prefersReducedMotion();
    setLayers((current) => {
      // No artwork is established yet (nothing painted, e.g. a redirect during the first
      // render): show the destination's own background straight away and fade out what was
      // visible, so artwork that never appeared can't appear later.
      const base = current[0];
      if (base && base.key !== key && (base.phase === 'revealing' || !base.painted)) {
        const outgoing = current
          .filter((layer) => layer.phase !== 'loading')
          // Every outgoing layer is marked the same way whatever its phase, so replayed updates
          // agree; each layer hides only artwork that never painted (data-art-unseen).
          .map((layer): Layer => ({ ...layer, phase: 'leaving', artHidden: true }));
        return [{ ...incoming, phase: 'revealing' }, ...(instant ? [] : outgoing)];
      }
      // A destination that was never painted is dropped instead of flashing stale artwork.
      const kept = current.filter((layer) => layer.phase !== 'loading' || layer.key === key);
      const top = kept[kept.length - 1];
      if (top?.key === key && top.phase !== 'leaving') {
        return kept.length === current.length ? current : kept;
      }
      // Painted artwork only ever moves toward the current page: anything fading in for a page
      // the learner already left fades back out. Returning to a page still on screen reverses.
      // A layer left before it was established isn't faded back in; it keeps leaving.
      const target = kept.map((layer) => (layer.artHidden ? null : layer.key)).lastIndexOf(key);
      const next = kept.flatMap((layer, index): Layer[] => {
        if (index === target) return [layer.phase === 'leaving' ? { ...layer, phase: 'entering' } : layer];
        const outgoing = target === -1 ? layer.phase === 'entering' : index > target;
        if (!outgoing) return [layer];
        return instant ? [] : [{ ...layer, phase: 'leaving' }];
      });
      return target === -1 ? [...next, incoming] : next;
    });
  }, [key, theme.renderBackground, coveredLocationKey]);

  // Leaving a covered entry for a page with the same background (a module's completion
  // page, say) doesn't run the effect above, but other art may still sit under the scene.
  useLayoutEffect(() => {
    const left = shownLocationKey.current;
    if (left === locationKey || coveredLocationKey.current === null || coveredLocationKey.current !== left) return;
    const id = nextId.current;
    nextId.current += 1;
    setLayers((current) => showOnly(current, { id, key, render: theme.renderBackground, phase: 'loading' }));
  }, [locationKey, key, theme.renderBackground, coveredLocationKey]);

  useLayoutEffect(() => {
    shownLocationKey.current = locationKey;
  }, [locationKey]);

  const handleReady = useCallback((id: number, fadeArt: boolean) => {
    const instant = prefersReducedMotion();
    setLayers((current) => {
      const index = current.findIndex((layer) => layer.id === id);
      if (index === -1) return current;
      const { phase, artShown } = current[index];
      if (phase === 'revealing') {
        if (artShown) return current;
        const next: Layer = instant || !fadeArt ? { ...current[index], phase: 'settled' } : { ...current[index], artShown: true };
        return current.map((layer) => (layer.id === id ? next : layer));
      }
      if (phase !== 'loading') return current;
      if (instant) return [{ ...current[index], phase: 'settled' }, ...current.slice(index + 1)];
      return current.map((layer) => (layer.id === id ? { ...layer, phase: 'entering' } : layer));
    });
  }, []);

  const handlePainted = useCallback((id: number) => {
    setLayers((current) => (current.some((layer) => layer.id === id && layer.phase === 'settled' && !layer.painted)
      ? current.map((layer) => (layer.id === id ? { ...layer, painted: true } : layer))
      : current));
  }, []);

  const handleLeft = useCallback((id: number) => {
    setLayers((current) => (current.some((layer) => layer.id === id && layer.phase === 'leaving')
      ? current.filter((layer) => layer.id !== id)
      : current));
  }, []);

  const handleEntered = useCallback((id: number) => {
    setLayers((current) => {
      const index = current.findIndex((layer) => layer.id === id);
      if (index === -1 || (current[index].phase !== 'entering' && current[index].phase !== 'revealing')) return current;
      // It was visible throughout its fade, so it has been painted.
      return [{ ...current[index], phase: 'settled', painted: true }, ...current.slice(index + 1)];
    });
  }, []);

  return (
    <div
      className="pointer-events-none fixed inset-0 z-0"
      aria-hidden="true"
      style={{
        right: 'var(--app-scrollbar-width, 0px)',
        bottom: 'var(--app-scrollbar-height, 0px)',
      }}
    >
      {layers.map((layer) => (
        <BackgroundLayer
          key={layer.id}
          layer={layer}
          onReady={handleReady}
          onEntered={handleEntered}
          onLeft={handleLeft}
          onPainted={handlePainted}
        />
      ))}
    </div>
  );
};
